import { useEffect, useRef, useState } from "react";
import {
  createCollabProvider,
  resolveCollabWebsocketUrl,
  type CollabProviderHandle,
  type CollabProviderStatus,
} from "@justwrite/collab";
import type { Doc as YDoc } from "yjs";
import type { Awareness } from "y-protocols/awareness";
import { cloudAuthDesktopV2 } from "../services/cloudAuthDesktopV2";

const CARET_COLORS = ["#1e3a8a", "#9f1239", "#065f46", "#9a3412", "#5b21b6", "#0e7490"];

function pickColor(seed: string): string {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  return CARET_COLORS[hash % CARET_COLORS.length]!;
}

export type PremierLiveCollabState = {
  enabled: boolean;
  status: CollabProviderStatus;
  ydoc: YDoc | null;
  /** Awareness instance for CollaborationCaret (TipTap provider shape). */
  awareness: Awareness | null;
  caretUser: { name: string; color: string } | null;
  sessionKey: string;
};

/**
 * Connects to the Premier collab WS when VITE_COLLAB_WS_URL is set and the user is signed in.
 * Returns a Y.Doc + awareness for TipTap Collaboration / CollaborationCaret.
 */
export function usePremierLiveCollab(options: {
  bookId: string | null | undefined;
  chapterId: string | null | undefined;
}): PremierLiveCollabState {
  const bookId = options.bookId?.trim() || "";
  const chapterId = options.chapterId?.trim() || "";
  const [status, setStatus] = useState<CollabProviderStatus>("idle");
  const [ydoc, setYdoc] = useState<YDoc | null>(null);
  const [awareness, setAwareness] = useState<Awareness | null>(null);
  const [caretUser, setCaretUser] = useState<{ name: string; color: string } | null>(null);
  const [sessionKey, setSessionKey] = useState("local");
  const handleRef = useRef<CollabProviderHandle | null>(null);

  const wsUrl = resolveCollabWebsocketUrl();
  const enabled = Boolean(wsUrl && bookId && chapterId);

  useEffect(() => {
    if (!enabled || !wsUrl) {
      handleRef.current?.destroy();
      handleRef.current = null;
      setYdoc(null);
      setAwareness(null);
      setCaretUser(null);
      setStatus("idle");
      setSessionKey("local");
      return;
    }

    let cancelled = false;

    void (async () => {
      if (!cloudAuthDesktopV2.isSignedIn()) {
        setStatus("offline");
        return;
      }
      setStatus("connecting");
      try {
        const room = await cloudAuthDesktopV2.getCollabRoomToken(bookId, chapterId);
        if (cancelled) return;
        const url = room.websocketUrl || wsUrl;
        if (!url) {
          setStatus("offline");
          return;
        }
        const userId = cloudAuthDesktopV2.getUserId() || "author";
        const user = {
          name: userId.slice(0, 8),
          color: pickColor(`${bookId}:${userId}`),
        };
        handleRef.current?.destroy();
        const handle = createCollabProvider({
          bookId,
          chapterId,
          token: room.token,
          websocketUrl: url,
          user,
          onStatus: (next) => {
            if (!cancelled) setStatus(next);
          },
        });
        handleRef.current = handle;
        setYdoc(handle.doc);
        setAwareness(handle.awareness);
        setCaretUser(user);
        setSessionKey(`collab:${bookId}:${chapterId}`);
      } catch {
        if (!cancelled) {
          setStatus("error");
          setYdoc(null);
          setAwareness(null);
          setCaretUser(null);
          setSessionKey("local");
        }
      }
    })();

    return () => {
      cancelled = true;
      handleRef.current?.destroy();
      handleRef.current = null;
    };
  }, [enabled, wsUrl, bookId, chapterId]);

  return {
    enabled,
    status,
    ydoc,
    awareness,
    caretUser,
    sessionKey,
  };
}
